import {useMemo} from 'react'
import {ChatMessage, DataObjectId, Hub, SandboxTree} from 'yougile-model'
import {useUserProxy} from '@components/common/base/avatar'
import {useGuestUserProxy} from '@components/common/base/avatar/use-user-proxy'
import {ChatMessageData} from '@components/common/base/chat-message'
import {useUserName} from '@components/common/base/user-name'
import {EmojiUtils} from '@extensions/emoji-utils'
import {globals} from '@extensions/globals-base'
import {idTasksStore} from '@extensions/id-tasks/id-tasks-store'
import {MessageUtils} from '@extensions/message-utils'
import {extractFiles} from './extract-files'

export interface UseMessageDataOptions {
  box: SandboxTree
  msg?: ChatMessage
  task?: Hub
}

export const useMessageData = ({
  box,
  msg,
  task
}: UseMessageDataOptions): ChatMessageData | undefined => {
  const userId = msg?.from
  const guestId = msg?.properties['guestId'] as DataObjectId

  const user = useUserProxy(userId)
  const guest = useGuestUserProxy(guestId)

  const {name: userName, type: userNameType} = useUserName({
    userId,
    task,
    guestId,
    nameOverride: msg?.properties.displayName as string
  })

  const html = useMemo(
    () => msg && MessageUtils.renderHtmlForView(box, msg),
    [msg?.properties?.textHtml, msg?.text, idTasksStore.state.modified]
  )

  if (!msg) {
    return undefined
  }

  const text = msg.isTextHtml() ? msg.getTextHtml() : msg.text
  const emojiOnly = EmojiUtils.isShortEmoji(text)

  return {
    user: user || guest,
    html,
    userName: userName,
    userNameType: userNameType,
    timestamp: msg.timestamp,
    editedTimestamp: msg.edited?.timestamp,
    label: msg.label,
    emojiOnly,
    my: globals.currentUserId === userId,
    files: extractFiles(msg)
  }
}
